import { APUtilSPrintf } from './string.sprintf.util';

interface startCaseOptions {
  splitChar?: string;
  joinChar?: string;
  to?: 'upper' | 'lower';
}

export class APUtilString {
  static toUpperCase(str: string, replaceSpecialsChars = false) {
    if (replaceSpecialsChars) {
      return this._upperCase(str);
    } else {
      return str.toUpperCase();
    }
  }

  static toLowerCase(str: string, replaceSpecialsChars = false) {
    if (replaceSpecialsChars) {
      return this._lowerCase(str).replace(/ /g, ' ');
    } else {
      return str.toLowerCase();
    }
  }

  static upperFirst(str: string): string {
    return this._createCaseFirst('toUpperCase')(str);
  }

  static lowerFirst(str: string): string {
    return this._createCaseFirst('toLowerCase')(str);
  }

  static startCase = (
    str?: string | boolean,
    replaceSpecialsChars = false,
    { splitChar = ' ', joinChar = ' ', to = 'upper' }: startCaseOptions = {}
  ) => {
    const _str = str !== undefined ? str.toString() : '';

    if (_str) {
      if (replaceSpecialsChars) {
        return this._startCase(_str);
      } else {
        return _str
          .split(splitChar)
          .map((word) => {
            if (to === 'lower')
              return word.charAt(0).toLocaleLowerCase() + word.slice(1).toLowerCase();
            else return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
          })
          .join(joinChar);
      }
    }
    return '';
  };

  static toCamelCase(str: string) {
    return this._camelCase(str);
  }

  static toPascalCase(str: string) {
    return this._startCase(this._camelCase(str)).replace(/ /g, '');
  }
  static toKebabCase(str: string) {
    return this._kebabCase(str);
  }

  static toSnakeCase(str: string) {
    return this._snakeCase(str);
  }
  static toDotCase(str: string) {
    return this._lowerCase(str).replace(/ /g, '.');
  }

  static toSentenceCase(str: string) {
    return this._upperFirst(this._lowerCase(str));
  }

  static toSlugCase(str: string) {
    return this._lowerCase(str).replace(/ /g, '-');
  }

  static splitWords(str: string): string[] {
    return this._toWords(str);
  }

  static trimEnd(str: string, s: string): string {
    return str.endsWith(s) ? str.slice(0, -s.length) : str;
  }
  static trimStart(str: string, s: string): string {
    return str.startsWith(s) ? str.slice(s.length) : str;
  }

  static sprintf = APUtilSPrintf.sprintf;

  // private methods

  private static _toWords(str: string): string[] {
    return (
      str
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/[\s_-]+/g, ' ')
        .toLowerCase()
        .match(/[a-zà-öø-ÿ0-9]+/gi) || []
    );
  }

  private static _lowerCase(str: string): string {
    return this._toWords(str)
      .map((word) => word.toLowerCase())
      .join(' ');
  }

  private static _upperFirst(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }

  private static _camelCase(str: string): string {
    const words = this._toWords(str);
    return words
      .map((word, index) =>
        index === 0 ? word.toLowerCase() : this._upperFirst(word.toLowerCase())
      )
      .join('');
  }

  private static _kebabCase(str: string): string {
    return this._toWords(str).join('-');
  }

  private static _snakeCase(str: string): string {
    return this._toWords(str).join('_');
  }

  private static _startCase(str: string): string {
    return this._toWords(str)
      .map((word) => this._upperFirst(word))
      .join(' ');
  }

  private static _upperCase(str: string): string {
    return this._toWords(str).join(' ').toUpperCase();
  }

  private static _createCaseFirst = (methodName: string) => (string: string) => {
    if (!string) return '';
    const character = string.substr(0, 1);
    const trailing = string.slice(1);
    return (character[methodName as keyof string] as () => string)() + trailing;
  };
}
